import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLanguage } from "@/context/LanguageContext";
import { useNotice } from "@/context/NoticeContext";

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <ToggleGroup
      type="single"
      value={lang}
      onValueChange={(v) => v && setLang(v)}
      className={`rounded-full border border-[color:var(--border-subtle)] bg-white p-0.5 ${className}`}
      data-testid="language-toggle"
    >
      <ToggleGroupItem
        value="en"
        aria-label="English"
        data-testid="lang-en"
        className="h-8 rounded-full px-3 text-xs font-semibold data-[state=on]:bg-[var(--brand-burgundy)] data-[state=on]:text-white"
      >
        EN
      </ToggleGroupItem>
      <ToggleGroupItem
        value="es"
        aria-label="Español"
        data-testid="lang-es"
        className="h-8 rounded-full px-3 text-xs font-semibold data-[state=on]:bg-[var(--brand-burgundy)] data-[state=on]:text-white"
      >
        ES
      </ToggleGroupItem>
    </ToggleGroup>
  );
};

export const Navbar = () => {
  const { t } = useLanguage();
  const { openNotice } = useNotice();
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#services", label: t.nav.services },
    { href: "#about", label: t.nav.about },
    { href: "#portal", label: t.nav.portal },
    { href: "#footer", label: t.nav.contact },
  ];

  const goCta = () => {
    const el = document.getElementById("portal");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className="sticky top-3 z-50 px-3"
      data-testid="sticky-nav"
    >
      <nav
        className={`mx-auto flex w-[min(1180px,100%)] items-center justify-between gap-3 rounded-2xl border border-[color:var(--border-subtle)] px-3 py-2.5 backdrop-blur transition-shadow duration-300 sm:px-4 ${
          scrolled ? "bg-white/90 shadow-[var(--shadow-md)]" : "bg-white/80 shadow-[var(--shadow-sm)]"
        }`}
      >
        <a href="#home" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/assets/nina-mark.png" alt="Nina Latinos" className="h-9 w-9 object-contain" />
          <div className="hidden leading-none sm:block">
            <div className="font-heading text-lg font-bold tracking-tight text-[var(--brand-navy)]">NINA</div>
            <div className="text-[10px] font-medium tracking-wide text-[var(--brand-burgundy)]">Latinos Multi Services</div>
          </div>
        </a>

        <div className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-lg px-3 py-2 text-sm font-medium text-[var(--brand-charcoal)] transition-colors hover:bg-black/5 hover:text-[var(--brand-burgundy)]"
              data-testid={`nav-link-${l.href.replace('#','')}`}
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={openNotice}
            className="rounded-lg px-3 py-2 text-sm font-medium text-[var(--brand-charcoal)] transition-colors hover:bg-black/5 hover:text-[var(--brand-burgundy)]"
            data-testid="nav-link-notice"
          >
            {t.nav.notice}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <LangToggle className="hidden sm:inline-flex" />
          <Button
            onClick={goCta}
            className="hidden h-10 rounded-xl bg-[var(--brand-burgundy)] px-4 text-sm font-semibold text-white shadow-[var(--shadow-sm)] transition-colors hover:bg-[var(--brand-burgundy-dark)] active:scale-[0.98] md:inline-flex"
            data-testid="nav-cta"
          >
            {t.nav.cta}
          </Button>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[min(86vw,340px)] bg-white">
              <div className="mt-2 flex items-center gap-2.5">
                <img src="/assets/nina-mark.png" alt="Nina" className="h-9 w-9 object-contain" />
                <div className="font-heading text-lg font-bold text-[var(--brand-navy)]">NINA</div>
              </div>
              <div className="mt-6 flex flex-col gap-1">
                {links.map((l) => (
                  <SheetClose asChild key={l.href}>
                    <a
                      href={l.href}
                      className="rounded-lg px-3 py-3 text-base font-medium text-[var(--brand-charcoal)] transition-colors hover:bg-black/5"
                      data-testid={`mobile-nav-link-${l.href.replace('#','')}`}
                    >
                      {l.label}
                    </a>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <button
                    onClick={openNotice}
                    className="rounded-lg px-3 py-3 text-left text-base font-medium text-[var(--brand-charcoal)] transition-colors hover:bg-black/5"
                    data-testid="mobile-nav-link-notice"
                  >
                    {t.nav.notice}
                  </button>
                </SheetClose>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-sm font-medium text-[var(--muted-ink)]">{t.nav.langLabel}</span>
                <LangToggle />
              </div>
              <SheetClose asChild>
                <Button
                  onClick={goCta}
                  className="mt-6 h-11 w-full rounded-xl bg-[var(--brand-burgundy)] font-semibold text-white hover:bg-[var(--brand-burgundy-dark)]"
                  data-testid="mobile-nav-cta"
                >
                  {t.nav.cta}
                </Button>
              </SheetClose>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
